import { formatIngredient, type IngredientItem } from '../../shared/recipe';

export interface FieldChange {
  field: string;
  before: string;
  after: string;
}

export interface IngredientChange {
  kind: 'added' | 'removed' | 'changed';
  before?: string;
  after?: string;
}

const LABELS: Record<string, string> = {
  title: 'Title', summary: 'Summary', body_md: 'Notes', status: 'Status', favourite: 'Favourite', rating: 'Rating',
  source_url: 'Source URL', source_note: 'Source', tags: 'Tags', cover_media_id: 'Cover image',
};

const show = (v: unknown): string => {
  if (v === null || v === undefined || v === '') return '—';
  if (Array.isArray(v)) return v.length ? v.map(show).join(', ') : '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

function flatten(o: any, prefix = '', out: Record<string, unknown> = {}): Record<string, unknown> {
  if (o && typeof o === 'object' && !Array.isArray(o)) {
    for (const [k, v] of Object.entries(o)) flatten(v, prefix ? `${prefix}.${k}` : k, out);
  } else out[prefix] = o;
  return out;
}

/** Field-level diff between two card snapshots; ingredients are diffed as rows. */
export function diffSnapshots(a: any, b: any): { fields: FieldChange[]; ingredients: IngredientChange[]; steps: FieldChange[] } {
  const fields: FieldChange[] = [];
  for (const k of Object.keys(LABELS)) {
    if (show(a?.[k]) !== show(b?.[k])) fields.push({ field: LABELS[k], before: show(a?.[k]), after: show(b?.[k]) });
  }
  const da = { ...(a?.data ?? {}) };
  const db = { ...(b?.data ?? {}) };
  const ingA: IngredientItem[] = (da.ingredient_groups ?? []).flatMap((g: any) => g.items ?? []);
  const ingB: IngredientItem[] = (db.ingredient_groups ?? []).flatMap((g: any) => g.items ?? []);
  const stepsA: any[] = da.steps ?? [];
  const stepsB: any[] = db.steps ?? [];
  for (const d of [da, db]) {
    delete d.ingredient_groups;
    delete d.steps;
  }
  const fa = flatten(da);
  const fb = flatten(db);
  for (const k of new Set([...Object.keys(fa), ...Object.keys(fb)])) {
    if (show(fa[k]) !== show(fb[k])) fields.push({ field: k.replace(/_/g, ' '), before: show(fa[k]), after: show(fb[k]) });
  }

  const ingredients: IngredientChange[] = [];
  const key = (i: IngredientItem) => i.item.toLowerCase().trim();
  const mapA = new Map(ingA.map((i) => [key(i), i]));
  const mapB = new Map(ingB.map((i) => [key(i), i]));
  for (const [k, i] of mapA) {
    const j = mapB.get(k);
    if (!j) ingredients.push({ kind: 'removed', before: formatIngredient(i) });
    else if (formatIngredient(i) !== formatIngredient(j)) ingredients.push({ kind: 'changed', before: formatIngredient(i), after: formatIngredient(j) });
  }
  for (const [k, j] of mapB) if (!mapA.has(k)) ingredients.push({ kind: 'added', after: formatIngredient(j) });

  const steps: FieldChange[] = [];
  for (let i = 0; i < Math.max(stepsA.length, stepsB.length); i++) {
    const x = stepsA[i]?.text ?? '';
    const y = stepsB[i]?.text ?? '';
    if (x !== y) steps.push({ field: `Step ${i + 1}`, before: x || '—', after: y || '—' });
  }
  return { fields, ingredients, steps };
}
